import type { ViewController } from '@presentation/shell/NexxusShell';
import type { Bulletin } from '@shared/types/Bulletin';
import { fmtFCFA, fmtDate, fmtPeriode, esc } from '@presentation/shell/format';
import { ToastService } from '@presentation/components/Toast';
import { confirmAction } from '@presentation/components/ConfirmDialog';
import { buildBulletinHTML } from '@presentation/print/bulletinTemplate';
import { printHtml, previewDocument, downloadHtmlAsPdf } from '@presentation/print/PdfService';
import { downloadBlob, downloadCsv } from '@presentation/shell/download';
import { bulletinsToCsv } from '@domain/paie/BulletinCsv';
import { recordAuditEvent } from '@application/audit/AuditDirectoryService';
import { canMutateRecords } from '@application/access/ViewAccessPolicy';
import { onExclusive } from '@presentation/shell/viewDom';
import { periodeCouranteFromTenant } from '@presentation/shell/periodeCourante';
import { planifierCorrectionDoublons } from '@domain/paie/CorrectionDoublons';
import {
  corrigerDoublonsBulletins,
  type DepotEcritures,
} from '@application/usecases/CorrigerDoublonsBulletinsUseCase';
import { isModuleExcluded, PAIE_COMPTA_MODULE } from '@application/licence/ModuleGate';
import { loadComptesPaie } from '@application/comptabilite/ComptesPaieStore';
import { COMPTES_PAIE_PAR_DEFAUT } from '@domain/comptabilite/ComptesPaie';
import { loadPeriodesCloturees } from '@application/comptabilite/PeriodesCloturees';

function shiftPeriode(periode: string, delta: number): string {
  const [y, m] = periode.split('-').map(Number);
  const d = new Date(y ?? 2026, (m ?? 1) - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function statutBadge(statut: string | undefined): string {
  const map: Record<string, { bg: string; fg: string; label: string }> = {
    genere: { bg: '#F0F2F4', fg: '#8BA8B8', label: 'Généré' },
    valide: { bg: '#E3F4EC', fg: '#2E8B57', label: 'Validé' },
    paye: { bg: '#E7F5F8', fg: '#00819A', label: 'Payé' },
    annule: { bg: '#FBE9E7', fg: '#C0392B', label: 'Annulé' },
  };
  const s = map[statut ?? 'genere'] ?? map['genere']!;
  return `<span style="display:inline-block;padding:2px 10px;border-radius:999px;font-size:11px;font-weight:700;background:${s.bg};color:${s.fg};">${s.label}</span>`;
}

function envoiBadge(b: Bulletin): string {
  if (b.statut_envoi === 'envoye' && b.envoye_le) {
    return `<span style="display:inline-block;padding:2px 10px;border-radius:999px;font-size:11px;font-weight:700;background:#E3F4EC;color:#2E8B57;" title="E-mail préparé le ${esc(fmtDate(b.envoye_le))} (Nexxus ne sait pas si vous l’avez envoyé)">E-mail préparé</span>`;
  }
  return `<span style="display:inline-block;padding:2px 10px;border-radius:999px;font-size:11px;font-weight:700;background:#F0F2F4;color:#8BA8B8;">Non préparé</span>`;
}

export const bulletinsController: ViewController = async (section, ctx, shell) => {
  const [bulletins, salaries] = await Promise.all([ctx.bulletins.list(), ctx.salaries.list()]);
  const nameById = new Map(salaries.map((s) => [s.id, `${s.prenom} ${s.nom}`]));
  const salarieById = new Map(salaries.map((s) => [s.id, s]));
  const tenant = ctx.tenantContext.tryGetTenant();
  const canMutate = canMutateRecords(shell.permission);

  const buildPages = (list: Bulletin[]): string =>
    list
      .map((b) => {
        const sal = salarieById.get(b.salarie_id);
        return sal ? `<div class="bulletin-page">${buildBulletinHTML(b, sal, tenant)}</div>` : '';
      })
      .join('');

  const periodes = Array.from(new Set(bulletins.map((b) => b.periode))).sort((a, b) =>
    b.localeCompare(a),
  );
  let current = periodeCouranteFromTenant(tenant, { periodes });

  const tbody = section.querySelector<HTMLElement>('#bulletins-tbody');
  const empty = section.querySelector<HTMLElement>('#bulletins-empty');
  const count = section.querySelector('#count-bulletins');
  const periodeLabel = section.querySelector('#bulletins-periode');
  const periodeDisplay = section.querySelector('#periode-display-2');
  const monthPicker = section.querySelector<HTMLInputElement>('#bulletins-month-picker');
  const quickjump = section.querySelector<HTMLSelectElement>('#bulletins-period-quickjump');

  if (quickjump) {
    quickjump.innerHTML =
      '<option value=""> Périodes existantes…</option>' +
      periodes.map((p) => `<option value="${esc(p)}">${esc(fmtPeriode(p))}</option>`).join('');
  }

  const render = (): void => {
    const list = bulletins.filter((b) => b.periode === current);
    if (periodeLabel) periodeLabel.textContent = fmtPeriode(current);
    if (periodeDisplay) periodeDisplay.textContent = fmtPeriode(current);
    if (monthPicker) monthPicker.value = current;
    if (count) count.textContent = String(list.length);
    if (empty) empty.style.display = list.length === 0 ? '' : 'none';
    if (tbody) {
      tbody.innerHTML = list
        .map((b: Bulletin) => {
          const nom = b.nom_complet || nameById.get(b.salarie_id) || b.matricule || '—';
          const numero = `${b.matricule ?? ''}-${b.periode}`.replace(/^-/, '');
          return `<tr>
            <td class="col-bulletin-check rh-admin-only" style="text-align:center;"><input type="checkbox" data-bulletin="${esc(b.id)}"></td>
            <td class="mono">${esc(numero)}</td>
            <td><strong>${esc(nom)}</strong></td>
            <td class="num">${esc(fmtFCFA(b.net_a_payer))}</td>
            <td class="num">${esc(fmtFCFA(b.cout_employeur))}</td>
            <td>${statutBadge(b.statut)}</td>
            <td>${esc(fmtDate(b.date_emission || b.cree_le))}</td>
            <td>${envoiBadge(b)}</td>
            <td class="num"><button class="btn btn-ghost btn-sm" data-view-bulletin="${esc(b.id)}">Voir</button></td>
          </tr>`;
        })
        .join('');
    }
    syncSelectionUi();
  };

  const syncSelectionUi = (): void => {
    const checked = section.querySelectorAll<HTMLInputElement>(
      '#bulletins-tbody input[type=checkbox]:checked',
    ).length;
    const info = section.querySelector<HTMLElement>('#bulletins-selected-info');
    const countEl = section.querySelector('#bulletins-selected-count');
    const btn = section.querySelector<HTMLButtonElement>('#btn-print-selected');
    if (info) info.style.display = checked > 0 ? '' : 'none';
    if (countEl) countEl.textContent = String(checked);
    if (btn) btn.disabled = checked === 0;
  };

  /**
   * Doublons de bulletins (même salarié, même période). Décision du dirigeant du 22/10/2026 : on les supprime,
   * jamais un bulletin validé ou payé, avec sauvegarde automatique, confirmation et journal d'audit.
   */
  const doublonsBox = section.querySelector<HTMLElement>('#bulletins-doublons');
  const renderDoublons = (): void => {
    if (!doublonsBox) return;
    const plan = planifierCorrectionDoublons(bulletins);
    if (plan.groupes.length === 0) {
      doublonsBox.style.display = 'none';
      doublonsBox.innerHTML = '';
      return;
    }
    const lignes: string[] = [];
    if (plan.nbASupprimer > 0) {
      lignes.push(
        `<strong>${plan.nbASupprimer} bulletin(s) en double exact</strong> (même salarié, même période, mêmes montants) faussent les totaux, la comptabilité et les déclarations.`,
      );
    }
    if (plan.nbFigesIdentiques > 0) {
      lignes.push(
        `${plan.nbFigesIdentiques} doublon(s) déjà validé(s) ou payé(s) : jamais supprimé(s) automatiquement, à traiter à la main.`,
      );
    }
    if (plan.nbDifferents > 0) {
      lignes.push(
        `${plan.nbDifferents} bulletin(s) du même salarié et de la même période avec des montants différents : lancez « Calculer la paie » dans la saisie mensuelle pour les remplacer par un seul.`,
      );
    }
    const bouton =
      canMutate && plan.nbASupprimer > 0
        ? ` <button class="btn btn-secondary btn-sm" id="btn-corriger-doublons" type="button">Corriger les doublons</button>`
        : '';
    doublonsBox.innerHTML = `<div>${lignes.map((l) => `<p style="margin:0 0 6px;">${l}</p>`).join('')}</div>${bouton}`;
    doublonsBox.style.display = '';
  };

  const corrigerDoublons = async (): Promise<void> => {
    const plan = planifierCorrectionDoublons(bulletins);
    if (plan.nbASupprimer === 0) {
      ToastService.info('Aucun doublon exact à supprimer');
      return;
    }
    const detail = plan.groupes
      .filter((g) => g.aSupprimer.length > 0)
      .map((g) => {
        const nom =
          g.conserve.nom_complet ||
          nameById.get(g.salarieId) ||
          g.conserve.matricule ||
          g.salarieId;
        return `${nom} (${fmtPeriode(g.periode)}) : ${g.aSupprimer.length} en double`;
      })
      .join(' ; ');
    const ok = await confirmAction({
      host: section,
      title: 'Corriger les doublons de bulletins',
      message:
        `${plan.nbASupprimer} bulletin(s) en double seront supprimés (un seul bulletin est conservé par salarié et par période) ` +
        `et les écritures de paie concernées seront refaites. Une sauvegarde est téléchargée avant toute suppression. ` +
        `Les bulletins validés ou payés ne sont jamais supprimés. ${detail}`,
      confirmLabel: 'Supprimer les doublons',
    });
    if (!ok) return;
    const tid = ctx.tenantContext.tryGetTenant()?.id ?? 'default';
    const avecCompta = !isModuleExcluded(ctx, PAIE_COMPTA_MODULE);
    const [comptesPaie, cloturees] = avecCompta
      ? await Promise.all([loadComptesPaie(tid), loadPeriodesCloturees(tid)])
      : [COMPTES_PAIE_PAR_DEFAUT, [] as string[]];
    // Offre Paie seule : la paie ne touche pas à la comptabilité (aucun appel aux écritures).
    const sansEcritures: DepotEcritures = {
      list: async () => [],
      add: async () => undefined,
      delete: async () => undefined,
    };
    try {
      const r = await corrigerDoublonsBulletins(
        {
          bulletins: ctx.bulletins,
          ecritures: avecCompta ? ctx.ecritures : sansEcritures,
          salaries: ctx.salaries,
        },
        {
          comptesPaie,
          periodesCloturees: cloturees,
          sauvegarder: (donnees) => {
            const horodatage = new Date().toISOString();
            const contenu = JSON.stringify(
              { exporte_le: horodatage, motif: 'Correction des doublons de bulletins', ...donnees },
              null,
              2,
            );
            downloadBlob(
              new Blob([contenu], { type: 'application/json' }),
              `sauvegarde-doublons-bulletins-${horodatage.slice(0, 10)}.json`,
            );
          },
        },
      );
      const supprimes = new Set(r.supprimes.map((b) => b.id));
      for (let i = bulletins.length - 1; i >= 0; i -= 1) {
        if (supprimes.has(bulletins[i]!.id)) bulletins.splice(i, 1);
      }
      for (const b of r.supprimes) {
        await recordAuditEvent(ctx, {
          action: 'delete',
          entity: 'bulletin',
          entity_id: b.id,
          description: `Doublon de bulletin supprimé ${b.periode} — ${b.matricule ?? b.salarie_id} (un bulletin conservé)`,
        });
      }
      render();
      renderDoublons();
      const reste = r.ignores.length;
      ToastService.success(
        `${r.supprimes.length} doublon(s) supprimé(s), sauvegarde téléchargée` +
          (reste > 0
            ? ` — ${reste} groupe(s) non modifié(s) (période clôturée ou fiche introuvable)`
            : ''),
      );
    } catch (err) {
      ToastService.error(
        `Correction des doublons refusée, rien n'a été modifié : ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };
  onExclusive(doublonsBox, 'click', (e) => {
    if ((e.target as HTMLElement).closest('#btn-corriger-doublons')) void corrigerDoublons();
  });

  render();
  renderDoublons();

  const w = window as unknown as Record<string, unknown>;
  w['changePeriode'] = (delta: number) => {
    current = shiftPeriode(current, delta);
    render();
  };
  w['setPeriode'] = (value: string) => {
    if (value) {
      current = value;
      render();
    }
  };
  w['gotoCurrentMonth'] = () => {
    current = new Date().toISOString().slice(0, 7);
    render();
  };
  w['gotoOffsetMonth'] = (offset: number) => {
    current = shiftPeriode(new Date().toISOString().slice(0, 7), offset);
    render();
  };

  const selectedOrPeriod = (): Bulletin[] => {
    const ids = new Set(
      Array.from(
        section.querySelectorAll<HTMLInputElement>('#bulletins-tbody input[type=checkbox]:checked'),
      )
        .map((c) => c.dataset.bulletin)
        .filter((id): id is string => Boolean(id)),
    );
    const period = bulletins.filter((b) => b.periode === current);
    return ids.size > 0 ? period.filter((b) => ids.has(b.id)) : period;
  };

  w['validerBulletins'] = () => {
    if (!canMutate) {
      ToastService.warning('Validation réservée au gestionnaire RH et à l’administrateur');
      return;
    }
    void (async () => {
      const cibles = selectedOrPeriod().filter((b) => b.statut !== 'annule' && b.statut !== 'paye');
      const aValider = cibles.filter((b) => b.statut !== 'valide');
      if (aValider.length === 0) {
        ToastService.info('Aucun bulletin à valider sur cette sélection');
        return;
      }
      const ok = await confirmAction({
        host: section,
        title: 'Valider les bulletins',
        message: `${aValider.length} bulletin(s) de ${fmtPeriode(current)} passeront au statut validé.`,
        confirmLabel: 'Valider',
      });
      if (!ok) return;
      for (const b of aValider) {
        await ctx.bulletins.update({ ...b, statut: 'valide' });
        const idx = bulletins.findIndex((x) => x.id === b.id);
        if (idx >= 0) bulletins[idx] = { ...b, statut: 'valide' };
      }
      await recordAuditEvent(ctx, {
        action: 'update',
        entity: 'bulletin',
        description: `Validation de ${aValider.length} bulletin(s) — ${current}`,
      });
      render();
      ToastService.success(`${aValider.length} bulletin(s) validé(s)`);
      void import('../OnboardingChecklist').then(({ markOnboardingStep }) => {
        markOnboardingStep('bulletin');
      });
    })();
  };

  w['envoyerBulletins'] = () => {
    void (async () => {
      const cibles = selectedOrPeriod();
      if (cibles.length === 0) {
        ToastService.warning('Aucun bulletin à envoyer sur cette période');
        return;
      }
      const avecEmail = cibles.filter((b) => salarieById.get(b.salarie_id)?.email?.trim());
      const sansEmail = cibles.length - avecEmail.length;
      if (avecEmail.length === 0) {
        ToastService.warning('Aucun e-mail renseigné sur les fiches salariés concernées');
        return;
      }
      const destinataires = [
        ...new Set(
          avecEmail
            .map((b) => salarieById.get(b.salarie_id)?.email?.trim())
            .filter((email): email is string => Boolean(email)),
        ),
      ];
      ToastService.info(`Génération de ${avecEmail.length} PDF…`);
      const pdfOk: Bulletin[] = [];
      try {
        for (const b of avecEmail) {
          const sal = salarieById.get(b.salarie_id);
          if (!sal) continue;
          const filename = `Bulletin_${sal.nom}_${sal.prenom}_${b.periode}.pdf`.replace(
            /\s+/g,
            '_',
          );
          await downloadHtmlAsPdf(buildBulletinHTML(b, sal, tenant), filename);
          pdfOk.push(b);
        }
      } catch {
        ToastService.error('Échec de génération PDF — envoi annulé');
        return;
      }
      if (pdfOk.length === 0) {
        ToastService.warning('Aucun PDF généré');
        return;
      }
      const subject = `Bulletins de paie — ${fmtPeriode(current)}`;
      const body = [
        `Veuillez trouver ci-joint le(s) bulletin(s) de paie de ${fmtPeriode(current)}.`,
        `${pdfOk.length} bulletin(s) — les PDF viennent d’être téléchargés depuis Nexxus ERP.`,
        'Joignez-les à cet e-mail avant envoi.',
      ].join('\n');
      const href = `mailto:?bcc=${encodeURIComponent(destinataires.join(','))}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = href;
      const now = new Date().toISOString();
      for (const b of pdfOk) {
        const updated: Bulletin = { ...b, statut_envoi: 'envoye', envoye_le: now };
        if (canMutate) {
          await ctx.bulletins.update(updated);
        }
        const idx = bulletins.findIndex((x) => x.id === b.id);
        if (idx >= 0) bulletins[idx] = updated;
      }
      await recordAuditEvent(ctx, {
        action: 'export',
        entity: 'bulletin',
        description: `Export PDF + messagerie de ${pdfOk.length} bulletin(s) — ${current}`,
      });
      render();
      ToastService.success(
        sansEmail > 0
          ? `${pdfOk.length} PDF téléchargé(s), messagerie ouverte — ${sansEmail} sans e-mail`
          : `${pdfOk.length} PDF téléchargé(s) — joignez-les puis envoyez depuis votre messagerie`,
      );
    })();
  };
  w['printAllBulletins'] = () => {
    const list = bulletins.filter((b) => b.periode === current);
    if (list.length === 0) {
      ToastService.warning('Aucun bulletin sur cette période');
      return;
    }
    printHtml(buildPages(list), `${list.length} bulletin(s) — ${fmtPeriode(current)}`);
  };
  w['printSelectedBulletins'] = () => {
    const ids = new Set(
      Array.from(
        section.querySelectorAll<HTMLInputElement>('#bulletins-tbody input[type=checkbox]:checked'),
      ).map((c) => c.dataset.bulletin),
    );
    const list = bulletins.filter((b) => b.periode === current && ids.has(b.id));
    if (list.length === 0) {
      ToastService.warning('Sélectionnez au moins un bulletin');
      return;
    }
    printHtml(buildPages(list), `${list.length} bulletin(s) — ${fmtPeriode(current)}`);
  };
  w['exportBulletinsCSV'] = () => {
    const list = selectedOrPeriod();
    if (list.length === 0) {
      ToastService.warning('Aucun bulletin à exporter sur cette période');
      return;
    }
    downloadCsv(bulletinsToCsv(list, nameById), `bulletins-${current}.csv`);
    ToastService.success(`${list.length} bulletin(s) exporté(s)`);
  };
  w['toggleAllBulletinsSelection'] = (checked: boolean) => {
    section
      .querySelectorAll<HTMLInputElement>('#bulletins-tbody input[type=checkbox]')
      .forEach((c) => (c.checked = checked));
    syncSelectionUi();
  };

  section.addEventListener('change', (e) => {
    if ((e.target as HTMLElement).matches?.('#bulletins-tbody input[type=checkbox]')) {
      syncSelectionUi();
    }
  });

  onExclusive(tbody, 'click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-view-bulletin]');
    if (btn) {
      const b = bulletins.find((x) => x.id === btn.dataset.viewBulletin);
      const sal = b ? salarieById.get(b.salarie_id) : undefined;
      if (b && sal) {
        const filename = `Bulletin_${sal.nom}_${sal.prenom}_${b.periode}.pdf`.replace(/\s+/g, '_');
        previewDocument(
          buildBulletinHTML(b, sal, tenant),
          filename,
          `Bulletin — ${sal.prenom} ${sal.nom} · ${fmtPeriode(b.periode)}`,
        );
      } else if (b) {
        ToastService.info(`Bulletin ${b.periode} — net ${fmtFCFA(b.net_a_payer)}`);
      }
    }
  });
};
